import TeletelFlowsAPI from '../../api/teletel/flows';

const state = {
  records: [],
  meta: { selectorErrors: 0, selectorLastError: null },
  runs: [],
  conversationRuns: {},
  engines: [],
  availableFlows: [],
  templates: [],
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isFetchingRuns: false,
  },
};

export const getters = {
  getFlows: $state => $state.records,
  getFlow: $state => id => $state.records.find(f => f.id === Number(id)),
  getMeta: $state => $state.meta,
  getRuns: $state => $state.runs,
  getEngines: $state => $state.engines,
  getAvailableFlows: $state => $state.availableFlows,
  getTemplates: $state => $state.templates,
  getUIFlags: $state => $state.uiFlags,
  getConversationRun: $state => conversationId =>
    $state.conversationRuns[conversationId] || null,
};

export const actions = {
  async get({ commit }) {
    commit('setUIFlag', { isFetching: true });
    try {
      const { data } = await TeletelFlowsAPI.get();
      commit('setFlows', data.payload);
      commit('setMeta', data.meta || {});
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },
  async show({ commit }, id) {
    const { data } = await TeletelFlowsAPI.show(id);
    commit('upsertFlow', data.payload);
    return data.payload;
  },
  async create({ commit }, flow) {
    commit('setUIFlag', { isSaving: true });
    try {
      const { data } = await TeletelFlowsAPI.create(flow);
      commit('upsertFlow', data.payload);
      return data.payload;
    } finally {
      commit('setUIFlag', { isSaving: false });
    }
  },
  async update({ commit }, { id, ...flow }) {
    commit('setUIFlag', { isSaving: true });
    try {
      const { data } = await TeletelFlowsAPI.update(id, flow);
      commit('upsertFlow', data.payload);
      return data.payload;
    } finally {
      commit('setUIFlag', { isSaving: false });
    }
  },
  async delete({ commit }, id) {
    await TeletelFlowsAPI.delete(id);
    commit('removeFlow', id);
  },
  async publish({ commit }, id) {
    const { data } = await TeletelFlowsAPI.publish(id);
    commit('upsertFlow', data.payload);
    return data.payload;
  },
  async deactivate({ commit }, id) {
    const { data } = await TeletelFlowsAPI.deactivate(id);
    commit('upsertFlow', data.payload);
    return data.payload;
  },
  async fetchRuns({ commit }, params = {}) {
    commit('setUIFlag', { isFetchingRuns: true });
    try {
      const { data } = await TeletelFlowsAPI.getRuns(params);
      commit('setRuns', data.payload);
      return data.payload;
    } finally {
      commit('setUIFlag', { isFetchingRuns: false });
    }
  },
  async fetchRun(_, runId) {
    const { data } = await TeletelFlowsAPI.getRun(runId);
    return data.payload;
  },
  async fetchConversationRun({ commit }, conversationId) {
    const { data } = await TeletelFlowsAPI.getRuns({
      conversation_id: conversationId,
    });
    commit('setConversationRun', {
      conversationId,
      run: (data.payload || [])[0] || null,
    });
    return (data.payload || [])[0] || null;
  },
  async stopRun({ commit }, { runId, conversationId }) {
    const { data } = await TeletelFlowsAPI.stopRun(runId);
    if (conversationId)
      commit('setConversationRun', { conversationId, run: data.payload });
    return data.payload;
  },
  async resumeRun({ commit }, { runId, conversationId }) {
    const { data } = await TeletelFlowsAPI.resumeRun(runId);
    if (conversationId)
      commit('setConversationRun', { conversationId, run: data.payload });
    return data.payload;
  },
  async pauseRun({ commit }, { runId, conversationId }) {
    const { data } = await TeletelFlowsAPI.pauseRun(runId);
    if (conversationId)
      commit('setConversationRun', { conversationId, run: data.payload });
    return data.payload;
  },
  async retryRun({ commit }, { runId, conversationId }) {
    const { data } = await TeletelFlowsAPI.retryRun(runId);
    if (conversationId)
      commit('setConversationRun', { conversationId, run: data.payload });
    return data.payload;
  },
  async fetchAvailableFlows({ commit }) {
    const { data } = await TeletelFlowsAPI.getAvailable();
    commit('setAvailableFlows', data.payload);
    return data.payload;
  },
  async startFlow(
    { commit },
    { flowId, conversationDisplayId, operationUuid }
  ) {
    const { data } = await TeletelFlowsAPI.trigger(flowId, {
      conversationDisplayId,
      operationUuid,
    });
    commit('setConversationRun', {
      conversationId: conversationDisplayId,
      run: null,
    });
    return data.payload;
  },
  async fetchEngines({ commit }) {
    const { data } = await TeletelFlowsAPI.getEngines();
    commit('setEngines', data.payload);
  },
  async setEngine({ commit }, { inboxId, engine }) {
    const { data } = await TeletelFlowsAPI.updateEngine(inboxId, engine);
    commit('upsertEngine', data.payload);
    return data.payload;
  },
  async fetchTemplates({ commit }) {
    commit('setUIFlag', { isFetching: true });
    try {
      const { data } = await TeletelFlowsAPI.getTemplates();
      commit('setTemplates', data.payload);
    } finally {
      commit('setUIFlag', { isFetching: false });
    }
  },
  async instantiateTemplate({ commit }, { templateId, name }) {
    const { data } = await TeletelFlowsAPI.instantiateTemplate(
      templateId,
      name
    );
    commit('upsertFlow', data.payload);
    return data.payload;
  },
};

export const mutations = {
  setUIFlag($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },
  setFlows($state, flows) {
    $state.records = flows;
  },
  setMeta($state, meta) {
    $state.meta = {
      selectorErrors: meta.selector_errors || 0,
      selectorLastError: meta.selector_last_error || null,
    };
  },
  upsertFlow($state, flow) {
    const index = $state.records.findIndex(f => f.id === flow.id);
    if (index === -1) $state.records.push(flow);
    else $state.records.splice(index, 1, flow);
  },
  removeFlow($state, id) {
    $state.records = $state.records.filter(f => f.id !== Number(id));
  },
  setRuns($state, runs) {
    $state.runs = runs;
  },
  setConversationRun($state, { conversationId, run }) {
    $state.conversationRuns = {
      ...$state.conversationRuns,
      [conversationId]: run,
    };
  },
  setEngines($state, engines) {
    $state.engines = engines;
  },
  setAvailableFlows($state, flows) {
    $state.availableFlows = flows;
  },
  setTemplates($state, templates) {
    $state.templates = templates;
  },
  upsertEngine($state, engine) {
    const index = $state.engines.findIndex(e => e.inbox_id === engine.inbox_id);
    if (index === -1) $state.engines.push(engine);
    else $state.engines.splice(index, 1, engine);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
